const requestHeaderStates = new WeakMap();

let responseObserverState = null;

function installResponseObserverProxy() {
    if (responseObserverState) return responseObserverState;
    if (typeof globalThis.fetch !== 'function') return null;

    const originalFetch = globalThis.fetch;
    const state = {
        originalFetch,
        observers: new Set(),
        proxy: null,
    };
    state.proxy = async (...args) => {
        const response = await state.originalFetch(...args);
        for (const observer of [...state.observers]) {
            try {
                observer({ input: args[0], init: args[1], response });
            } catch {
                // Response observation must never affect the reporting request.
            }
        }
        return response;
    };
    globalThis.fetch = state.proxy;
    responseObserverState = state;
    return state;
}

export function registerDevExpressResponseObserver(observer) {
    if (typeof observer !== 'function') return () => {};

    const state = installResponseObserverProxy();
    if (!state) return () => {};

    state.observers.add(observer);
    let disposed = false;
    return () => {
        if (disposed) return;
        disposed = true;
        state.observers.delete(observer);
        if (state.observers.size === 0 && globalThis.fetch === state.proxy) {
            globalThis.fetch = state.originalFetch;
            responseObserverState = null;
        }
    };
}

function applyHeaderScopes(fetchSetup, state) {
    const scopes = [...state.scopes.values()];
    const exclusiveScopes = scopes.filter((scope) => scope.exclusive);
    const activeScopes = exclusiveScopes.length ? exclusiveScopes : scopes;
    const headers = exclusiveScopes.length ? {} : { ...state.baseHeaders };
    for (const scope of activeScopes) {
        Object.assign(headers, scope.headers);
    }

    if (Object.keys(headers).length > 0) {
        fetchSetup.fetchSettings.headers = headers;
    } else {
        delete fetchSetup.fetchSettings.headers;
    }
}

/**
 * DevExpress React RequestOptions does not accept custom headers. Register them
 * on the fetch request manager for the lifetime of a mounted viewer/designer.
 */
export function registerDevExpressRequestHeaders(fetchSetup, headers, { exclusive = false } = {}) {
    const normalizedHeaders = Object.fromEntries(
        Object.entries(headers || {}).filter(([, value]) => value != null && value !== ''),
    );
    if (Object.keys(normalizedHeaders).length === 0 && !exclusive) return () => {};

    let state = requestHeaderStates.get(fetchSetup);
    if (!state) {
        state = {
            baseHeaders: { ...(fetchSetup.fetchSettings?.headers || {}) },
            scopes: new Map(),
        };
        requestHeaderStates.set(fetchSetup, state);
    }

    fetchSetup.fetchSettings ||= {};
    const scope = Symbol('devexpress-request-headers');
    state.scopes.set(scope, { headers: normalizedHeaders, exclusive });
    applyHeaderScopes(fetchSetup, state);

    let disposed = false;
    return () => {
        if (disposed) return;
        disposed = true;

        const currentState = requestHeaderStates.get(fetchSetup);
        if (!currentState) return;
        currentState.scopes.delete(scope);
        applyHeaderScopes(fetchSetup, currentState);
        if (currentState.scopes.size === 0) {
            requestHeaderStates.delete(fetchSetup);
        }
    };
}
